import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import DateInput from "./DateInput";

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  de: {
    title: "Buchungsanfrage · Preisinformation",
    lead: "Füllen Sie Ihre Kontaktdaten und Reisedetails aus – wir melden uns mit einem unverbindlichen Angebot.",
    first: "Vorname", last: "Nachname", email: "E-Mail-Adresse", phone: "Telefonnummer (optional)",
    ci: "Anreise", co: "Abreise", nights: "Übernachtungen",
    adults: "Anzahl Erwachsene", kids: "Anzahl Kinder", ages: "Alter der Kinder (falls angegeben)",
    accommodation: "Gewünschte Unterkunft", purpose: "Reisezweck", meals: "Verpflegung",
    parking: "Parkplatz benötigt?", ref: "Wie haben Sie von uns erfahren?",
    message: "Ihre Nachricht oder besondere Wünsche",
    consent: "Ich stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu.",
    submit: "🏠 Jetzt Buchungsanfrage senden",
    yes: "Ja", no: "Nein", none: "— bitte wählen —",
    thanks_title: "Vielen Dank für Ihre Anfrage!",
    thanks_text: "Wir melden uns in der Regel innerhalb von 2 Stunden mit einem persönlichen Angebot.",
    ok: "Anfrage gesendet",
    errRequired: "Bitte alle Pflichtfelder ausfüllen",
    errDates: "Abreise muss nach Anreise liegen",
    errConsent: "Bitte DSGVO-Zustimmung aktivieren",
    purposes: ["Urlaub","Geschäftsreise","Montage","Familienbesuch","Sonstiges"],
    meal_opts: ["Frühstück","Mittagessen","Abendessen"],
    ref_opts: ["Google","Booking.com","Airbnb","Empfehlung","Zufällig gefunden","Wiederkehrender Gast","Sonstiges"],
  },
  en: {
    title: "Booking inquiry · Price information",
    lead: "Fill in your contact details and travel data – we'll get back to you with a non-binding offer.",
    first: "First name", last: "Last name", email: "Email address", phone: "Phone number (optional)",
    ci: "Check-in", co: "Check-out", nights: "Nights",
    adults: "Adults", kids: "Children", ages: "Ages of children (if any)",
    accommodation: "Preferred accommodation", purpose: "Purpose of trip", meals: "Meals",
    parking: "Parking needed?", ref: "How did you hear about us?",
    message: "Your message or special requests",
    consent: "I consent to the processing of my data to handle this inquiry.",
    submit: "🏠 Send booking inquiry now",
    yes: "Yes", no: "No", none: "— please choose —",
    thanks_title: "Thank you for your inquiry!",
    thanks_text: "We usually get back to you within 2 hours with a personal offer.",
    ok: "Inquiry sent",
    errRequired: "Please fill all required fields",
    errDates: "Check-out must be after check-in",
    errConsent: "Please accept the data-processing consent",
    purposes: ["Holiday","Business","Workers/Assembly","Family visit","Other"],
    meal_opts: ["Breakfast","Lunch","Dinner"],
    ref_opts: ["Google","Booking.com","Airbnb","Recommendation","Found by chance","Returning guest","Other"],
  },
};

export default function PublicInquiry() {
  const params = new URLSearchParams(window.location.search);
  const langParam = (params.get("lang") || "").toLowerCase();
  // Default: DE (95% der Gäste). EN nur wenn explizit ?lang=en gesetzt.
  const initialLang = langParam === "en" ? "en" : "de";
  const embed = params.get("embed") === "1";
  const showHeader = params.get("header") !== "0" && !embed; // Header standardmäßig weg im Embed
  const [lang, setLang] = useState(initialLang);
  const t = T[lang];
  const [rooms, setRooms] = useState([]);
  const rootRef = useRef(null);
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    check_in: "", check_out: "", adults: 2, children: 0, children_ages: "",
    accommodation: "", purpose: "", meals: [], parking: "", referral: "", message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/inquiry/rooms`).then(r => setRooms(r.data.rooms || [])).catch(() => setRooms([]));
  }, []);

  // Embed-Modus: Höhe an Eltern-Homepage per postMessage schicken (Auto-Resize im iframe)
  useEffect(() => {
    if (!embed || !rootRef.current) return;
    const send = () => {
      const h = document.documentElement.scrollHeight;
      window.parent?.postMessage({ type: "msb-inquiry-height", height: h }, "*");
    };
    const ro = new ResizeObserver(send);
    ro.observe(document.body);
    send();
    return () => ro.disconnect();
  }, [embed]);

  const nights = useMemo(() => {
    if (!f.check_in || !f.check_out) return 0;
    const a = new Date(f.check_in), b = new Date(f.check_out);
    const d = (b - a) / (1000*60*60*24);
    return d > 0 ? d : 0;
  }, [f.check_in, f.check_out]);

  const toggleMeal = (m) => setF(x => ({...x, meals: x.meals.includes(m) ? x.meals.filter(y => y!==m) : [...x.meals, m]}));

  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email || !f.check_in || !f.check_out) return toast.error(t.errRequired);
    if (f.check_out <= f.check_in) return toast.error(t.errDates);
    if (!f.consent) return toast.error(t.errConsent);
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/inquiry/submit`, {...f, lang});
      setDone(true);
      toast.success(t.ok);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Senden");
    } finally { setSubmitting(false); }
  };

  if (done) return (
    <div ref={rootRef} style={{
      minHeight: embed ? "auto" : "100vh",
      background: embed ? "transparent" : "linear-gradient(135deg,#f0f9f4 0%,#eef6ff 100%)",
      display:"grid", placeItems:"center", padding: embed ? "0" : "24px",
      fontFamily:"'Work Sans',Arial,sans-serif"
    }}>
      <div style={{maxWidth:520,background:"#fff",borderRadius:20,padding:"36px 32px",boxShadow:"0 4px 20px rgba(0,0,0,.08)",textAlign:"center",width:"100%"}}>
        <div style={{fontSize:64,marginBottom:12}}>💚</div>
        <div style={{fontSize:22,fontWeight:800,color:"#2d5a3d",marginBottom:10}}>{t.thanks_title}</div>
        <div style={{fontSize:15,color:"#4b5563",lineHeight:1.6}}>{t.thanks_text}</div>
        <div style={{marginTop:26,fontSize:13,color:"#94a3b8"}}>MainSpessartBlick · Ringstraße 22b · 97816 Lohr am Main</div>
      </div>
    </div>
  );

  const inp = "w-full h-11 rounded-lg border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-sm";
  const lbl = "block text-sm font-semibold text-slate-700 mb-1";

  return (
    <div ref={rootRef} style={{
      minHeight: embed ? "auto" : "100vh",
      background: embed ? "transparent" : "linear-gradient(135deg,#f0f9f4 0%,#eef6ff 100%)",
      padding: embed ? "0" : "24px 16px",
      fontFamily:"'Work Sans',Arial,sans-serif"
    }} data-testid="public-inquiry-page">
      <div className={embed ? "max-w-2xl mx-auto" : "max-w-2xl mx-auto"}>
        {showHeader && (
          <div style={{background:"linear-gradient(135deg,#2d5a3d 0%,#3f7a54 100%)",borderRadius:16,padding:"22px 24px",color:"#fff",marginBottom:16}}>
            <div style={{fontSize:22,fontWeight:800,letterSpacing:".3px"}}>🏠 MainSpessartBlick</div>
            <div style={{fontSize:13,opacity:.9,marginTop:4}}>📍 Ringstraße 22b · 97816 Lohr am Main</div>
          </div>
        )}
        {/* Language toggle */}
        <div className="flex justify-end mb-3 gap-2">
          <button onClick={() => setLang("de")} className={`px-3 py-1 rounded-full text-xs font-semibold ${lang==="de"?"bg-emerald-600 text-white":"bg-white text-slate-600 border"}`} data-testid="inq-lang-de">DE</button>
          <button onClick={() => setLang("en")} className={`px-3 py-1 rounded-full text-xs font-semibold ${lang==="en"?"bg-emerald-600 text-white":"bg-white text-slate-600 border"}`} data-testid="inq-lang-en">EN</button>
        </div>
        {/* Card */}
        <div className={embed ? "bg-white rounded-2xl p-6 sm:p-8" : "bg-white rounded-2xl shadow-sm p-6 sm:p-8"}>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">{t.title}</h1>
          <p className="text-sm text-slate-600 mb-6">{t.lead}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>{t.first} *</label>
              <input className={inp} value={f.first_name} onChange={e=>setF({...f, first_name: e.target.value})} data-testid="inq-first-name"/>
            </div>
            <div>
              <label className={lbl}>{t.last} *</label>
              <input className={inp} value={f.last_name} onChange={e=>setF({...f, last_name: e.target.value})} data-testid="inq-last-name"/>
            </div>
            <div>
              <label className={lbl}>{t.email} *</label>
              <input type="email" className={inp} value={f.email} onChange={e=>setF({...f, email: e.target.value})} data-testid="inq-email"/>
            </div>
            <div>
              <label className={lbl}>{t.phone}</label>
              <input className={inp} value={f.phone} onChange={e=>setF({...f, phone: e.target.value})} placeholder="+49 …" data-testid="inq-phone"/>
            </div>
            <div>
              <label className={lbl}>{t.ci} *</label>
              <DateInput value={f.check_in} onChange={(v)=>setF({...f, check_in: v})} required min="today" testid="inq-check-in"/>
            </div>
            <div>
              <label className={lbl}>{t.co} *</label>
              <DateInput value={f.check_out} onChange={(v)=>setF({...f, check_out: v})} required min="today" testid="inq-check-out"/>
            </div>
          </div>

          {nights > 0 && (
            <div className="mt-3 text-center text-sm text-emerald-700 bg-emerald-50 rounded-lg py-2 font-semibold">
              = {nights} {t.nights}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            <div>
              <label className={lbl}>{t.adults} *</label>
              <input type="number" min="1" className={inp} value={f.adults} onChange={e=>setF({...f, adults: parseInt(e.target.value)||1})} data-testid="inq-adults"/>
            </div>
            <div>
              <label className={lbl}>{t.kids}</label>
              <input type="number" min="0" className={inp} value={f.children} onChange={e=>setF({...f, children: parseInt(e.target.value)||0})} data-testid="inq-children"/>
            </div>
          </div>
          {f.children > 0 && (
            <div className="mt-4">
              <label className={lbl}>{t.ages}</label>
              <input className={inp} value={f.children_ages} onChange={e=>setF({...f, children_ages: e.target.value})} placeholder="z.B. 3, 7" data-testid="inq-children-ages"/>
            </div>
          )}

          <div className="mt-5">
            <label className={lbl}>{t.accommodation}</label>
            <select className={inp} value={f.accommodation} onChange={e=>setF({...f, accommodation: e.target.value})} data-testid="inq-accommodation">
              <option value="">{t.none}</option>
              {rooms.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div className="mt-4">
            <label className={lbl}>{t.purpose}</label>
            <select className={inp} value={f.purpose} onChange={e=>setF({...f, purpose: e.target.value})} data-testid="inq-purpose">
              <option value="">{t.none}</option>
              {t.purposes.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="mt-4">
            <label className={lbl}>{t.meals}</label>
            <div className="flex flex-wrap gap-2">
              {t.meal_opts.map(m => (
                <button key={m} type="button" onClick={()=>toggleMeal(m)} data-testid={`inq-meal-${m}`}
                  className={`px-3 py-2 rounded-full text-sm font-semibold border transition ${f.meals.includes(m) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className={lbl}>{t.parking}</label>
            <div className="flex gap-2">
              {["Ja","Nein"].map(o => (
                <button key={o} type="button" onClick={()=>setF({...f, parking: o})} data-testid={`inq-parking-${o}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${f.parking===o ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  {lang==="en" ? (o==="Ja"?"Yes":"No") : o}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className={lbl}>{t.ref}</label>
            <select className={inp} value={f.referral} onChange={e=>setF({...f, referral: e.target.value})} data-testid="inq-referral">
              <option value="">{t.none}</option>
              {t.ref_opts.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div className="mt-4">
            <label className={lbl}>{t.message}</label>
            <textarea rows="3" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-sm"
              value={f.message} onChange={e=>setF({...f, message: e.target.value})} data-testid="inq-message"/>
          </div>

          <label className="mt-5 flex items-start gap-2 cursor-pointer" data-testid="inq-consent-wrapper">
            <input type="checkbox" checked={f.consent} onChange={e=>setF({...f, consent: e.target.checked})} className="mt-1 h-4 w-4 accent-emerald-600" data-testid="inq-consent"/>
            <span className="text-xs text-slate-600">{t.consent} *</span>
          </label>

          <button onClick={submit} disabled={submitting} data-testid="inq-submit"
            style={{
              background: submitting ? "#4b7d5b" : "#2d5a3d",
              boxShadow: "0 4px 12px rgba(45,90,61,.35), inset 0 -2px 0 rgba(0,0,0,.15)",
            }}
            onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = "#25482f"; }}
            onMouseLeave={e => { if (!submitting) e.currentTarget.style.background = "#2d5a3d"; }}
            className="mt-6 w-full h-14 rounded-xl text-white font-bold text-lg tracking-wide transition disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {submitting ? "…" : t.submit}
          </button>

          <div className="mt-6 text-center text-xs text-slate-400">
            MainSpessartBlick · +49 176 25799945 · info@mainspessartblick.de
          </div>
        </div>
      </div>
    </div>
  );
}
